import React, { useEffect, useRef, useState } from "react";
import cn from "classnames";
import { ShipmentType } from "entities/shared/shipmentCard.types";
import { ShipmentModel } from "features/shared/shipmentCard.types";
import { usePanelViews } from "features/shared/usePanelViews.hook";
import { authStore } from "entities/Auth";
import { searchFilterStore } from "entities/SearchFilter";
import { ShipmentTypeChecker } from "entities/shared/utils";
import { useAppSelector } from "shared/lib/redux";
import { useTranslation } from "shared/localization/localization";
import { Button } from "shared/ui/Button";
import { AlertIcon, SearchIcon } from "shared/ui/Icons";
import { Preloader } from "shared/ui/Preloader";
import styles from "./Search.module.scss";

import { useAirRequestStrategy } from "../../model/hooks/airRequestStrategy.hook";
import { useAirSearch } from "../../model/hooks/airSearch.hook";
import { useParcelRequestStrategy } from "../../model/hooks/parcelRequestStrategy.hook";
import { useParcelSearch } from "../../model/hooks/parcelSearch.hook";
import { useRailRequestStrategy } from "../../model/hooks/railRequestStrategy.hook";
import { useRailSearch } from "../../model/hooks/railSearch.hook";
import { useRoadRequestStrategy } from "../../model/hooks/roadRequestStrategy.hook";
import { useRoadSearch } from "../../model/hooks/roadSearch.hook";
import { useSeaRequestStrategy } from "../../model/hooks/seaRequestStrategy.hook";
import { useSeaSearch } from "../../model/hooks/seaSearch.hook";
import { SearchFilter } from "../SearchFilter";

type SearchProps = {
  openedCardReference: string | null;
  openCard: (card: ShipmentModel) => void;
};

export const Search: RFC<SearchProps> = ({ openedCardReference, openCard }) => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isShipmentTypeSelectionError, setIsShipmentTypeSelectionError] = useState(false);
  const { showMainView, clearViewHistory, showIntroView } = usePanelViews();

  const isAuthenticated = useAppSelector(authStore.selectors.isAuthenticated);
  const shipmentType = useAppSelector(searchFilterStore.selectors.shipment);
  const isShipmentTypeConfirmed = useAppSelector(searchFilterStore.selectors.isShipmentTypeConfirmed);
  const isGlobalTracking = useAppSelector(searchFilterStore.selectors.isGlobalTracking);
  const isGlobalSearchButton = isGlobalTracking && !isShipmentTypeConfirmed;
  const isAirSearchButton = isShipmentTypeConfirmed && ShipmentTypeChecker.isAir(shipmentType);

  useEffect(() => {
    if (isShipmentTypeConfirmed) setIsShipmentTypeSelectionError(false);
  }, [isShipmentTypeConfirmed]);

  const { isSeaSearchLoading } = useSeaSearch({ isAuthenticated });
  const { isAirSearchLoading } = useAirSearch({ isAuthenticated });
  const { isRailSearchLoading } = useRailSearch({ isAuthenticated });
  const { isRoadSearchLoading } = useRoadSearch({ isAuthenticated });
  const { isParcelSearchLoading } = useParcelSearch({ isAuthenticated });

  const { handleSearch: handleSeaSearch } = useSeaRequestStrategy({
    searchValue,
    setErrorMessage,
    openCard,
    showMainView,
    clearViewHistory,
    showIntroView,
    isReferenceEqualToOpenedCard: (reference: string) => reference === openedCardReference,
  });

  const { handleSearch: handleAirSearch } = useAirRequestStrategy({
    searchValue,
    setErrorMessage,
    openCard,
    showMainView,
    clearViewHistory,
    showIntroView,
    isReferenceEqualToOpenedCard: (reference: string) => reference === openedCardReference,
  });

  const { handleSearch: handleRailSearch } = useRailRequestStrategy({
    searchValue,
    setErrorMessage,
    openCard,
    showMainView,
    clearViewHistory,
    showIntroView,
    isReferenceEqualToOpenedCard: (reference: string) => reference === openedCardReference,
  });

  const { handleSearch: handleRoadSearch } = useRoadRequestStrategy({
    searchValue,
    setErrorMessage,
    openCard,
    showMainView,
    clearViewHistory,
    showIntroView,
    isReferenceEqualToOpenedCard: (reference: string) => reference === openedCardReference,
  });

  const { handleSearch: handleParcelSearch } = useParcelRequestStrategy({
    searchValue,
    setErrorMessage,
    openCard,
    showMainView,
    showIntroView,
    clearViewHistory,
    isReferenceEqualToOpenedCard: (reference: string) => reference === openedCardReference,
  });
  const isLoading =
    isSeaSearchLoading || isAirSearchLoading || isRailSearchLoading || isRoadSearchLoading || isParcelSearchLoading;

  const textInput = useRef<HTMLInputElement | null>(null);

  const handleSearch = () => {
    if (!isShipmentTypeConfirmed) {
      if (!searchValue) {
        setIsShipmentTypeSelectionError(false);
        setErrorMessage("The tracking number is required");
        return;
      }
      setIsShipmentTypeSelectionError(true);
      setErrorMessage("Please select a shipment type");
      return;
    }
    setIsShipmentTypeSelectionError(false);
    switch (shipmentType) {
      case ShipmentType.SEA:
        handleSeaSearch(searchValue);
        break;
      case ShipmentType.AIR:
        handleAirSearch(searchValue);
        break;
      case ShipmentType.RAIL:
        handleRailSearch(searchValue);
        break;
      case ShipmentType.ROAD:
        handleRoadSearch(searchValue);
        break;
      case ShipmentType.PARCEL:
        handleParcelSearch(searchValue);
        break;
    }
  };

  const onSubmit = () => {
    handleSearch();
    textInput.current?.focus();
  };

  const onChangeHandler: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    if (isLoading) return;
    setSearchValue(e.target.value);
  };

  const onKeydownHandler: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (!isLoading && e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  return (
    <div className={styles.search}>
      <div
        className={cn(styles.form, {
          [styles.loading]: isSeaSearchLoading || isAirSearchLoading || isRailSearchLoading || isRoadSearchLoading,
        })}
      >
        <div className={styles.inputContainer}>
          <input
            data-test-id="search-input"
            className={cn(errorMessage && !isShipmentTypeSelectionError && styles.error)}
            type="text"
            value={searchValue}
            disabled={isLoading}
            ref={textInput}
            placeholder={t("Shipment number")}
            onChange={onChangeHandler}
            onKeyDown={onKeydownHandler}
          />
        </div>
        <SearchFilter isShipmentTypeError={isShipmentTypeSelectionError} />
        <Button
          data-test-id="search-button"
          disabled={isLoading}
          onClick={() => onSubmit()}
          className={cn(
            styles.searchBtn,
            isAirSearchButton && styles.searchBtnAir,
            isGlobalSearchButton && styles.searchBtnGlobal
          )}
        >
          {isLoading ? (
            <Preloader />
          ) : (
            <>
              <SearchIcon />
              <span className={styles.searchBtnText}>{t("Search")}</span>
            </>
          )}
        </Button>
      </div>

      {errorMessage && (
        <div className={styles.message}>
          <span className={styles.iconWrapper}></span>
          <AlertIcon />
          <span className={styles.text}>{t(errorMessage)}</span>
        </div>
      )}
    </div>
  );
};
